import { defineComponent as _defineComponent } from 'vue'
import { FallbackAttachmentType, fallbackAttachmentSizeMapping } from '@@/bits/attachments'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { getEditorObject } from '@@/bits/tldraw/editor'
import { blankUser } from '@@/bits/user_model'
import { setupVueApp } from '@@/bits/vue'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard'
import { WHITEBOARD_ATTACHMENT_PADDING } from '@@/bits/whiteboard_no_dependencies'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWhiteboardUploadStore } from '@@/pinia/whiteboard_upload'
import type { Post, PostAttributes } from '@@/types'
import ContentPicker from '@@/vuecomponents/ContentPicker.vue'
import SurfacePostPoll from '@@/vuecomponents/SurfacePostPoll.vue'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { LinkHelpers, getDisplayAttributes } from '@padlet/beethoven-client'
import { uniqueId } from 'lodash-es'
import { storeToRefs } from 'pinia'
import type { Editor } from 'tldraw'
import { computed, watch } from 'vue'

// Content picker

export default /*#__PURE__*/_defineComponent({
  __name: 'WhiteboardAttachmentPicker',
  setup(__props) {

const surfaceContentPickerStore = useSurfaceContentPickerStore()
const surfaceStore = useSurfaceStore()
const whiteboardUploadStore = useWhiteboardUploadStore()
const { contentSources } = usePostContentPickerSources()
const { xPostContentPicker } = storeToRefs(surfaceContentPickerStore)
const googleDriveUploadKey = uniqueId('google-drive-upload-key-')
const xSearchBar = computed(() => {
  return contentSources.value
    .flat()
    .filter((source) => source.visible)
    .some((source) =>
      [
        ContentPickerSource.ImageSearch,
        ContentPickerSource.GifSearch,
        ContentPickerSource.VideoSearch,
        ContentPickerSource.AudioSearch,
        ContentPickerSource.WebBrowser,
        ContentPickerSource.LinkInput,
        ContentPickerSource.AiImageSearch,
      ].includes(source.type as ContentPickerSource),
    )
})

function hidePostContentPicker(): void {
  // TODO
}

const surfaceAttachmentStore = useSurfaceAttachmentsStore()
const surfacePostsStore = useSurfacePostsStore()

const { newestPostCid } = storeToRefs(surfacePostsStore)
const newestPost = computed(() => surfacePostsStore.postEntitiesByCid[newestPostCid.value])
const newestPostId = computed(() => newestPost.value?.id)

function onUploadReject(): void {
  // TODO: Implement
}

function onUploadError(): void {
  // TODO: Implement
}

function onUploadProgress(): void {
  // Ignore for now
}

function updateActiveDraft(postAttributes: PostAttributes): void {
  surfacePostsStore.createPostImmediately({
    attributes: {
      ...postAttributes,
    },
  })
}

function uploadFileActiveDraft(payload): void {
  whiteboardUploadStore.startNewFileUpload(payload.file)
}

function onUploadStart(payload): void {
  // Not being triggered but still need to pass the prop
  // Real upload is done in uploadFileActiveDraft
}

async function onUploadDone(url: string): Promise<void> {
  surfacePostsStore.createPostImmediately({
    attributes: {
      attachment: url,
      wish_content: {
        attachment_props: {
          url,
        },
        is_processed: false,
      },
    },
  })
}

function showExtensionRequired(): void {
  // TODO: Implement
}

function setCaption(): void {
  // TODO: Implement
}

function selectLink(url: string, payload: any): void {
  whiteboardUploadStore.startNewUrlUpload(url, payload)
}

// Only after the newest post is created, we can create the attachment shape in TLDraw
watch(newestPostId, async () => {
  if (!newestPostId.value) return
  const editor = getEditorObject()
  if (!editor) return
  const post = newestPost.value
  if (!post) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  if (post.id == null) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  if (post.top != null) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  // Create post for attachment
  if (post.attachment || post.wish_content?.attachment_props?.url) {
    createPostForAttachment(editor, post)
  } else if (post.wish_content?.attachment_props.type === 'poll') {
    createPostForPoll(editor, post)
  }
})

function throwErrorForMissingDataInNewestPost(): void {
  throw new Error('Missing data in newest post')
}

async function createPostForAttachment(editor: Editor, post: Post): Promise<void> {
  const url: string = post.wish_content?.attachment_props?.url ?? post.attachment
  const link = await surfaceAttachmentStore.fetchAndStoreLinkAttributes(url) // Eagerly fetch the link
  if (!link) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  const attributes = await getDisplayAttributes(link, {
    width: CANVAS_WIDTH / 2,
  })
  if (!attributes) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  let width = attributes.image_width
  let height = attributes.image_height
  if (width == null || height == null) {
    throwErrorForMissingDataInNewestPost()
    return
  }

  let ar = attributes.image_aspect_ratio
  if (attributes.is_fallback) {
    const fallbackAttachmentType = (() => {
      // if (isBrokenPadletLink.value || isFallbackPeekLink.value) return FallbackAttachmentType.BrokenPadletLink
      if (!link) return FallbackAttachmentType.BrokenLink
      if (LinkHelpers.isAudio(link)) return FallbackAttachmentType.Audio
      if (LinkHelpers.isVideo(link)) return FallbackAttachmentType.Video
      if (LinkHelpers.isPhoto(link)) return FallbackAttachmentType.Image
      if (LinkHelpers.isFile(link)) return FallbackAttachmentType.File
      if (LinkHelpers.isExcelSpreadsheet(link)) return FallbackAttachmentType.Spreadsheet
      if (LinkHelpers.isPowerpointPresentation(link)) return FallbackAttachmentType.Presentation
      if (LinkHelpers.isGoogleDoc(link)) return FallbackAttachmentType.GoogleDocument
      if (LinkHelpers.isDocument(link)) return FallbackAttachmentType.TextDocument
      if (LinkHelpers.isPage(link)) return FallbackAttachmentType.Link
      return FallbackAttachmentType.Link
    })()

    ar =
      fallbackAttachmentSizeMapping[fallbackAttachmentType].width /
      fallbackAttachmentSizeMapping[fallbackAttachmentType].height
  }
  if (link?.provider_name?.toLowerCase() === 'youtube') {
    ar = 16 / 9
  }
  height = width / ar

  if (width > CANVAS_WIDTH / 2) {
    width = CANVAS_WIDTH / 2
    height = Math.floor(width / ar)
  }
  if (height > CANVAS_HEIGHT / 2) {
    height = CANVAS_HEIGHT / 2
    width = Math.floor(height * ar)
  }
  editor.createShape({
    type: 'padlet-attachment-shape',
    x: CANVAS_WIDTH / 2 - width / 2,
    y: CANVAS_HEIGHT / 2 - height / 2,
    props: {
      postId: post.id,
      w: width + WHITEBOARD_ATTACHMENT_PADDING * 2,
      h: height + WHITEBOARD_ATTACHMENT_PADDING * 2,
    },
  })
  surfacePostsStore.savePost({
    attributes: {
      ...post,
      top: CANVAS_HEIGHT / 2 - height / 2,
      left: CANVAS_WIDTH / 2 - width / 2,
    },
  })
  whiteboardUploadStore.finishUploading(url)
}

async function createPostForPoll(editor: Editor, post: Post) {
  if (!post) {
    throwErrorForMissingDataInNewestPost()
    return
  }
  const { width, height } = await precalculateSizeForPoll(post)
  editor.createShape({
    type: 'padlet-attachment-shape',
    x: CANVAS_WIDTH / 2 - CANVAS_WIDTH / 3 / 2,
    y: CANVAS_HEIGHT / 2 - CANVAS_HEIGHT / 3 / 2,
    props: {
      postId: post.id,
      // w: CANVAS_WIDTH / 3,
      w: width + WHITEBOARD_ATTACHMENT_PADDING * 2,
      h: height + WHITEBOARD_ATTACHMENT_PADDING * 2,
      isPoll: true,
    },
  })
  surfacePostsStore.savePost({
    attributes: {
      ...post,
      top: CANVAS_HEIGHT / 2 - CANVAS_WIDTH / 3 / 2,
      left: CANVAS_WIDTH / 2 - CANVAS_HEIGHT / 3 / 2,
    },
  })
}

async function precalculateSizeForPoll(post: Post): Promise<{ width: number; height: number }> {
  // Render SurfacePostPoll for poll to get the size
  const poll = post.wish_content?.attachment_props
  if (!poll) {
    throwErrorForMissingDataInNewestPost()
    return { width: 0, height: 0 }
  }

  // Poll width is 1/3 of the canvas width by default
  const pollWidth = CANVAS_WIDTH / 3

  /**
   * Calculate poll height
   */

  // Create element on Dom
  const pollEl = document.createElement('div')
  pollEl.id = uniqueId('poll-')
  const pollVueApp = document.createElement('div')
  pollVueApp.id = uniqueId('poll-vue-app-')
  pollEl.setAttribute(
    'style',
    `width: ${pollWidth}px; visibility: hidden; position: absolute; top: -9999px; left: -9999px;`,
  )
  pollEl.appendChild(pollVueApp)
  document.body.appendChild(pollEl)

  // Render poll
  const temporaryPollApp = await setupVueApp({
    el: `#${pollVueApp.id}`,
    usePinia: false,
    disableAccessibilityChecks: true,
    rootComponent: SurfacePostPoll,
    otherVueOptions: {
      parent: (window as any).app,
      render: function (h: any) {
        return h(SurfacePostPoll, {
          props: {
            poll,
            pollCaption: post.attachment_caption,
            post,
            user: blankUser,
            darkMode: true,
          },
        })
      },
      methods: {
        handleOnLoaded: function () {},
      },
    },
  })
  // Get rendered height
  const pollHeight = pollEl.clientHeight

  // Clean up
  temporaryPollApp?.$destroy()
  document.body.removeChild(pollEl)

  // Return size
  return { width: pollWidth, height: pollHeight }
}

return { __sfc: true,surfaceContentPickerStore, surfaceStore, whiteboardUploadStore, contentSources, xPostContentPicker, googleDriveUploadKey, xSearchBar, hidePostContentPicker, surfaceAttachmentStore, surfacePostsStore, newestPostCid, newestPost, newestPostId, onUploadReject, onUploadError, onUploadProgress, updateActiveDraft, uploadFileActiveDraft, onUploadStart, onUploadDone, showExtensionRequired, setCaption, selectLink, throwErrorForMissingDataInNewestPost, createPostForAttachment, createPostForPoll, precalculateSizeForPoll, dir, device, recordWhitelistedClick, ContentPicker }
}

})